import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, ShieldCheck, MapTrifold } from "@phosphor-icons/react";
import Logo, { TRENT_LOGO_URL } from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Self-heal: clear any expired/invalid token that might have been left over,
  // so a bad cached token cannot trap the user on /login.
  useEffect(() => {
    try {
      localStorage.removeItem("trent_token");
    } catch {
      /* ignore storage errors */
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      // /login is for customers. Admins bounce to /admin-dashboard automatically.
      navigate(u.role === "admin" ? "/admin-dashboard" : "/customer-dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      {/* Left - branding */}
      <div className="hidden md:flex relative bg-[#1E3A5F] text-white p-12 flex-col justify-between overflow-hidden trent-noise">
        <div className="trent-grid-bg absolute inset-0 opacity-20" />
        <div className="relative">
          <div className="bg-white rounded-md px-4 py-3 inline-flex">
            <img src={TRENT_LOGO_URL} alt="Trent" className="h-12 w-auto" />
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/60">
            Property Inspections · Ireland
          </div>
        </div>

        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#16A34A] mb-4">
            Built for Ireland
          </div>
          <h1 className="trent-heading text-4xl md:text-5xl font-black leading-[1.05] tracking-tighter mb-6">
            Inventory, inspect, report.<br />
            <span className="text-[#16A34A]">In one workflow.</span>
          </h1>
          <p className="text-white/70 max-w-md text-sm leading-relaxed">
            A precise tool for letting agents, landlords and property managers. From Eircode to BER
            rating, every detail captured. Every report ready for tribunal.
          </p>
        </div>

        <div className="relative grid grid-cols-3 gap-4 text-xs">
          <div className="border border-white/15 p-4">
            <ShieldCheck size={22} className="text-[#16A34A] mb-2" weight="duotone" />
            <div className="font-semibold mb-1">RTB-ready</div>
            <div className="text-white/50 text-[11px]">Tribunal-grade documentation</div>
          </div>
          <div className="border border-white/15 p-4">
            <MapTrifold size={22} className="text-[#16A34A] mb-2" weight="duotone" />
            <div className="font-semibold mb-1">Irish-spec</div>
            <div className="text-white/50 text-[11px]">Eircode · BER · RTB</div>
          </div>
          <div className="border border-white/15 p-4">
            <div className="font-black text-2xl text-[#16A34A] mb-1">7</div>
            <div className="font-semibold mb-1">Steps</div>
            <div className="text-white/50 text-[11px]">From landlord to PDF</div>
          </div>
        </div>
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form
          onSubmit={submit}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B7280] mb-3">
            Trent Property Management
          </div>
          <h2 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#1E3A5F] mb-2">
            Customer Sign In
          </h2>
          <p className="text-sm text-[#6B7280] mb-8">
            Access your assigned properties, inspection reports, photos and signatures.
          </p>

          <label className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="trent-input mt-1 mb-4"
            required
            data-testid="login-email-input"
          />

          <label className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="trent-input mt-1 mb-2"
            required
            data-testid="login-password-input"
          />

          {error && (
            <div className="my-3 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FCA5A5] px-3 py-2 rounded-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="trent-btn-primary w-full mt-4"
            data-testid="login-submit-btn"
          >
            {loading ? "Signing in..." : "Sign in"} <ArrowRight weight="bold" size={16} />
          </button>

          <div className="mt-6 text-sm text-[#6B7280]">
            New customer?{" "}
            <Link to="/signup" className="text-[#1E3A5F] font-semibold underline" data-testid="register-link">
              Create an account
            </Link>
          </div>

          <div className="mt-3 text-sm text-[#6B7280]">
            Are you an administrator?{" "}
            <Link to="/admin-login" className="text-[#1E3A5F] font-semibold underline" data-testid="goto-admin-login">
              Admin Sign In →
            </Link>
          </div>

          <div className="mt-3 text-sm text-[#6B7280]">
            <Link to="/" className="text-[#1E3A5F] font-semibold underline" data-testid="login-back-link">
              ← Back to homepage
            </Link>
          </div>

          <div className="mt-10 text-[10px] uppercase tracking-[0.3em] text-[#6B7280]">
            Customer portal · Trent Property Management
          </div>
        </form>
      </div>
    </div>
  );
}
